'use client';
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getStrapiMedia } from "../utils/api-helpers";
import { STRAPI_UPLOADS_BASE_URL } from "../utils/strapi-config";


interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    preHeading: string;
    buttonText: string;
    buttonUrl: string;
    phone: string;
    title: string;
    description: string;
    picture: Picture;
    trustedBy: Picture;
  };
}

export default function Hero({ data }: HeroProps) {
  const pictureAttributes = data?.picture?.data?.attributes;
  const heroUrl = pictureAttributes?.url
    ? getStrapiMedia(pictureAttributes.url)
    : null;
  const trustedAttributes = data?.trustedBy?.data?.attributes;
  const trustedByUrl = trustedAttributes?.url
    ? getStrapiMedia(trustedAttributes.url)
    : null;
  const mediaPath = pictureAttributes?.url ?? "";
  const isVideo = /\.(mp4|webm|mov)$/i.test(mediaPath);
  const pathname = usePathname();
  const shouldOffset =
    pathname?.startsWith("/destinations") || pathname === "/destinations";
  const sectionPadding = shouldOffset
    ? "pt-[88px] sm:pt-[104px] dt:pt-[128px]"
    : "";

  return (
    <div>
    <section
      id="hero"
      className={`h-screen bg-cover bg-no-repeat bg-center flex flex-col justify-between items-center text-center text-white relative ${sectionPadding}`}
    >
      {heroUrl && !isVideo && (
        <Image
          src={heroUrl}
          alt={pictureAttributes?.alternativeText || "Hero background"}
          className="absolute top-0 left-0 h-full w-full object-cover z-[1]"
          priority
          fill
          sizes="100vw"
        />
      )}
      {heroUrl && isVideo && (
      <video className="absolute top-0 left-0 w-full h-full object-cover z-[1]" autoPlay muted loop playsInline preload="metadata">
        <source src={heroUrl} type="video/mp4" />
        </video>)}
      <div className="flex-1"></div>
      <div className="px-1/2 dt:px-0 dt:w-1/2 flex-1 z-[2]">
        <div className="mb-1/2 text-3/4 leading-3/4 dt:text-1.5 dt:leading-1.5 uppercase font-bold tracking-widest font-breit">{data.preHeading}</div>
        <h1 className="tracking-wider font-bold uppercase text-2.25 leading-2.25 dt:text-[4.5rem] dt:leading-[4.5rem]">{data.title}</h1>
        <h2 className="text-1 leading-1 dt:text-1.5 dt:leading-1.5 mt-1/2 dt:mt-1">{data.description}</h2>
        <div className="mt-2 relative dt:bottom-auto flex flex-col dt:flex-row items-center dt:mt-1.5 justify-center">
        <Link
        href={data.buttonUrl}
        className="bg-red hover:bg-blue text-white px-1.5 py-1 rounded-1/2 uppercase font-bold text-1 leading-1 tracking-wider"
      >
        {data.buttonText}
      </Link>
      <span className="tracking-wider uppercase ml-1/2 mt-1/2 dt:mt-0"> or call
      <a className="font-bold" href={`tel: ${data.phone}`}> {data.phone}</a>
      </span>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-end pb-1 dt:hidden">

      </div>
      {trustedByUrl && (
        <div className="flex-1 z-[2] hidden dt:flex flex-col justify-end pb-1">
          <div className="mb-1/2 uppercase font-bold text-3/4 leading-1 tracking-widest">Trusted by</div>
          <Image
            src={trustedByUrl}
            alt={trustedAttributes?.alternativeText || "Trusted by"}
            className="h-6 w-auto mx-auto"
            width={400}
            height={100}
          />
        </div>
      )}
    </section>
    {trustedByUrl && (
      <div className="flex-1 flex dt:hidden flex-col items-center pb-2.5 pt-3/4">
          <div className="mb-1/2 uppercase font-bold text-3/4 leading-1 tracking-widest text-grey">Trusted by</div>
          <Image
            src={`${STRAPI_UPLOADS_BASE_URL}/partners_mob_1422142db7.svg`}
            alt={trustedAttributes?.alternativeText || "Trusted by"}
            className="w-full h-auto mx-auto px-[.5rem]"
            width={800}
            height={200}
          />
        </div>
    )}
    </div>
  );
}
